import { useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput, { toInternationalPhoneDigits, isValidPhone } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ERRORS: Record<string, string> = {
  already_registered:
    'Ya existe una cuenta con ese RUC o WhatsApp. Si es tuya, pedile el link de acceso al bot por WhatsApp.',
  invalid_phone: 'El número de WhatsApp no es válido. Usá el formato 0991 123 456.',
  rate_limit: 'Demasiados intentos. Probá de nuevo en un rato.',
  validation_error: 'Revisá los datos del formulario.',
};

export function AppRegisterPage() {
  const [, navigate] = useLocation();
  const [ruc, setRuc] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [cedula, setCedula] = useState('');
  const [personType, setPersonType] = useState<'fisica' | 'juridica'>('fisica');
  const [phoneCountry, setPhoneCountry] = useState('PY');
  const [phoneNational, setPhoneNational] = useState('');
  const [consent, setConsent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const phoneValid = isValidPhone(phoneCountry, phoneNational);

  const mutation = useMutation({
    mutationFn: () =>
      api('/api/app/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          ruc: ruc.trim(),
          razonSocial: razonSocial.trim(),
          fullName: fullName.trim(),
          email: email.trim(),
          cedula: cedula.trim() || undefined,
          personType,
          whatsappNumber: toInternationalPhoneDigits(phoneCountry, phoneNational),
          consent,
        }),
      }),
    onError: (e) => {
      const code = e instanceof ApiCallError ? e.body.error : 'unknown';
      setErrorMsg(ERRORS[code] ?? 'No pudimos completar el registro. Probá de nuevo.');
    },
  });

  const canSubmit =
    ruc.trim().length >= 5 &&
    razonSocial.trim().length >= 2 &&
    fullName.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    phoneValid &&
    consent &&
    !mutation.isPending;

  if (mutation.isSuccess) {
    return (
      <Shell>
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <CheckCircle2 className="size-12 text-success" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">¡Registro recibido!</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Estamos verificando tu cuenta. En cuanto la activemos te mandamos el link de
                acceso por WhatsApp. Mientras tanto, ya podés mandarnos tus comprobantes al chat.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/app/login')}>
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>Creá tu cuenta</CardTitle>
          <CardDescription>
            Liquidá tus impuestos con IA. Te pedimos unos datos para empezar — un contador del
            equipo revisa y activa tu cuenta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              setErrorMsg(null);
              if (canSubmit) mutation.mutate();
            }}
          >
            <div className="grid gap-1.5">
              <Label>Tipo de contribuyente</Label>
              <Select value={personType} onValueChange={(v) => setPersonType(v as 'fisica' | 'juridica')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fisica">Persona física</SelectItem>
                  <SelectItem value="juridica">Empresa (persona jurídica)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="r-ruc">RUC</Label>
                <Input
                  id="r-ruc"
                  value={ruc}
                  onChange={(e) => setRuc(e.target.value)}
                  placeholder="80012345-6"
                  className="font-mono"
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="r-cedula">Cédula (opcional)</Label>
                <Input id="r-cedula" value={cedula} onChange={(e) => setCedula(e.target.value)} />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="r-razon">Razón social</Label>
              <Input
                id="r-razon"
                value={razonSocial}
                onChange={(e) => setRazonSocial(e.target.value)}
                placeholder="Como figura en SET"
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="r-name">Nombre y apellido</Label>
              <Input id="r-name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="r-email">Email</Label>
                <Input
                  id="r-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="r-wa">WhatsApp</Label>
                <PhoneInput
                  id="r-wa"
                  showLabel={false}
                  countryCode={phoneCountry}
                  phoneNumber={phoneNational}
                  onCountryCodeChange={setPhoneCountry}
                  onPhoneNumberChange={setPhoneNational}
                  placeholder="0991 123-456"
                  required
                />
                {phoneNational.trim().length > 0 && !phoneValid && (
                  <p className="text-xs text-destructive">Número de WhatsApp inválido.</p>
                )}
              </div>
            </div>

            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-muted-foreground">
              <Checkbox
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5"
              />
              <span>
                Acepto que Actuo procese mis datos para gestionar mis impuestos y autorizo a
                presentarlos ante la SET en mi nombre. Cumplimos la Ley 6534.
              </span>
            </label>

            {errorMsg && (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMsg}
              </p>
            )}

            <Button type="submit" disabled={!canSubmit} className="w-full">
              {mutation.isPending && <Loader2 className="animate-spin" />}
              {mutation.isPending ? 'Creando tu cuenta…' : 'Crear cuenta'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              ¿Ya tenés cuenta?{' '}
              <button
                type="button"
                onClick={() => navigate('/app/login')}
                className="font-medium text-primary hover:underline"
              >
                Iniciá sesión
              </button>
            </p>
          </form>
        </CardContent>
      </Card>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <img src="/actuo-wordmark.png" alt="Actuo" className="h-8" />
        </div>
        {children}
      </div>
    </div>
  );
}
